import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { EasterEggProvider } from './contexts/EasterEggContext'
import Layout from './components/layout/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import LoginForm from './components/auth/LoginForm'
import RegisterForm from './components/auth/RegisterForm'
import { Toaster } from 'react-hot-toast'

// Lazy-loaded page components
const CollectionPage = lazy(() => import('./pages/CollectionPage'))
const SetDetailPage = lazy(() => import('./pages/SetDetailPage'))
const SetsPage = lazy(() => import('./pages/SetsPage'))
const HomePage = lazy(() => import('./pages/HomePage'))
const TestPage = lazy(() => import('./pages/TestPage'))
const TestNDJSON = lazy(() => import('./pages/TestNDJSON'))
const OrdersPage = lazy(() => import('./pages/OrdersPage'))
const BinderListPage = lazy(() => import('./pages/BinderListPage'))
const BinderNewPage = lazy(() => import('./pages/BinderNewPage'))
const BinderBuilderPage = lazy(() => import('./pages/BinderBuilderPage'))
const BulkboxListPage = lazy(() => import('./pages/BulkboxListPage'))
const BulkboxEditorPage = lazy(() => import('./pages/BulkboxEditorPage'))
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'))
const DeckBuilderPage = lazy(() => import('./pages/DeckBuilderPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const Profile = lazy(() => import('./pages/Profile'))
const AdminPanel = lazy(() => import('./pages/AdminPanel'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminActivity = lazy(() => import('./pages/admin/Activity'))
const AdminSettings = lazy(() => import('./pages/admin/Settings'))
const AdminLeaderBreakdowns = lazy(() => import('./pages/admin/LeaderBreakdowns'))
const AdminLeaderBreakdownChart = lazy(() => import('./pages/admin/LeaderBreakdownChart'))
const AdminBlueprintBrowser = lazy(() => import('./pages/admin/BlueprintBrowser'))
const AdminFeedback = lazy(() => import('./pages/admin/Feedback'))
const AdminCardManager = lazy(() => import('./pages/admin/CardManager'))
const PublicLeaderBreakdown = lazy(() => import('./pages/PublicLeaderBreakdown'))
const WantsPage = lazy(() => import('./pages/WantsPage'))
const WantsDetailPage = lazy(() => import('./pages/WantsDetailPage'))
const WantsSharePage = lazy(() => import('./pages/WantsSharePage'))
const AllCardsPage = lazy(() => import('./pages/AllCardsPage'))
// AllCardsPageNDJSON merged into AllCardsPage (NDJSON cache-first)
const CardVariantsTest = lazy(() => import('./pages/CardVariantsTest'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const InventoryDashboardPage = lazy(() => import('./pages/InventoryDashboardPage'))
const InventoryIntakePage = lazy(() => import('./pages/InventoryIntakePage'))
const InventoryCardViewPage = lazy(() => import('./pages/InventoryCardViewPage'))
const InventoryLedgerPage = lazy(() => import('./pages/InventoryLedgerPage'))
const CompPage = lazy(() => import('./pages/CompPage'))
const BreakManagerPage = lazy(() => import('./pages/BreakManagerPage'))
const MetricsDashboardPage = lazy(() => import('./pages/MetricsDashboardPage'))
const SealedCollectionPage = lazy(() => import('./pages/SealedCollectionPage'))
const PlayerSetPage = lazy(() => import('./pages/PlayerSetPage'))
const PlayerSetStatsDetail = lazy(() => import('./pages/PlayerSetStatsDetail'))
const SlabsPage = lazy(() => import('./pages/SlabsPage'))
const CardDetailPage = lazy(() => import('./pages/CardDetailPage'))
const TampermonkeyGuidePage = lazy(() => import('./pages/TampermonkeyGuidePage'))
const PriceScanStatusPage = lazy(() => import('./pages/PriceScanStatusPage'))
const PublicStorePage = lazy(() => import('./pages/PublicStorePage'))
const StoreManagePage = lazy(() => import('./pages/StoreManagePage'))
const StoreManageLayout = lazy(() => import('./pages/store/StoreManageLayout'))
const StoreOverviewPage = lazy(() => import('./pages/store/StoreOverviewPage'))
const StoreInventoryPage = lazy(() => import('./pages/store/StoreInventoryPage'))
const StorePricingPage = lazy(() => import('./pages/store/StorePricingPage'))
const StoreOrdersPage = lazy(() => import('./pages/store/StoreOrdersPage'))
const StoreStorefrontPage = lazy(() => import('./pages/store/StoreStorefrontPage'))
const POSPage = lazy(() => import('./pages/POSPage'))
const StorePickingListPage = lazy(() => import('./pages/StorePickingListPage'))
const TradingDashboardPage = lazy(() => import('./pages/TradingDashboardPage'))
const TradingCardPage = lazy(() => import('./pages/TradingCardPage'))
const TradingPortfolioPage = lazy(() => import('./pages/TradingPortfolioPage'))
const TradingScannerPage = lazy(() => import('./pages/TradingScannerPage'))
const TradingHeatmapPage = lazy(() => import('./pages/TradingHeatmapPage'))
const TradingBuilderPage = lazy(() => import('./pages/TradingBuilderPage'))
const TradingSetBreakdownPage = lazy(() => import('./pages/TradingSetBreakdownPage'))
const TradingCardTraderPage = lazy(() => import('./pages/TradingCardTraderPage'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <EasterEggProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
          
          {/* Public wants share route */}
          <Route path="/wants/share/:shareCode" element={<WantsSharePage />} />
          
          {/* Public share routes (no layout) */}
          <Route path="/leader-breakdown/share/:shareCode" element={<PublicLeaderBreakdown />} />
          <Route path="/store/:slug" element={<PublicStorePage />} />

          {/* Kiosk-mode routes (no global Layout — own full-chrome).
              POS opts out of the global nav: it renders its own PosSessionBar
              and needs the full viewport (h-dvh) so the cart finalize button
              is always reachable. */}
          <Route path="/pos" element={
            <ProtectedRoute>
              <POSPage />
            </ProtectedRoute>
          } />

          {/* Main app with layout */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/test" element={<TestPage />} />
            <Route path="/test-ndjson" element={<TestNDJSON />} />
            <Route path="/card-variants-test" element={<CardVariantsTest />} />
            <Route path="/sets" element={<SetsPage />} />
            <Route path="/sets/:setId" element={<SetDetailPage />} />
            <Route path="/cards/:cardId" element={<CardDetailPage />} />
            
            {/* Protected routes */}
            <Route path="/collection" element={
              <ProtectedRoute>
                <CollectionPage />
              </ProtectedRoute>
            } />
            <Route path="/collection/all-cards" element={
              <ProtectedRoute>
                <AllCardsPage />
              </ProtectedRoute>
            } />
            <Route path="/collection/sealed" element={
              <ProtectedRoute>
                <SealedCollectionPage />
              </ProtectedRoute>
            } />
            <Route path="/binders" element={
              <ProtectedRoute>
                <BinderListPage />
              </ProtectedRoute>
            } />
            <Route path="/binders/new" element={
              <ProtectedRoute>
                <BinderNewPage />
              </ProtectedRoute>
            } />
            <Route path="/binders/:id" element={
              <ProtectedRoute>
                <BinderBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="/bulkboxes" element={
              <ProtectedRoute>
                <BulkboxListPage />
              </ProtectedRoute>
            } />
            <Route path="/bulkboxes/:id" element={
              <ProtectedRoute>
                <BulkboxEditorPage />
              </ProtectedRoute>
            } />
            <Route path="/collection/player-set" element={
              <ProtectedRoute>
                <PlayerSetPage />
              </ProtectedRoute>
            } />
            <Route path="/collection/player-set-stats/:setId" element={
              <ProtectedRoute>
                <PlayerSetStatsDetail />
              </ProtectedRoute>
            } />
            <Route path="/collection/:setId" element={
              <ProtectedRoute>
                <SetDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <InventoryDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/inventory/cards" element={
              <ProtectedRoute>
                <InventoryCardViewPage />
              </ProtectedRoute>
            } />
            <Route path="/inventory/ledger" element={
              <ProtectedRoute>
                <InventoryLedgerPage />
              </ProtectedRoute>
            } />
            <Route path="/inventory/comp" element={
              <ProtectedRoute>
                <CompPage />
              </ProtectedRoute>
            } />
            <Route path="/inventory/intake" element={
              <ProtectedRoute>
                <InventoryIntakePage />
              </ProtectedRoute>
            } />
            <Route path="/breaks" element={
              <ProtectedRoute>
                <BreakManagerPage />
              </ProtectedRoute>
            } />
            <Route path="/metrics" element={
              <ProtectedRoute>
                <MetricsDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/deck-builder" element={
              <ProtectedRoute>
                <DeckBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            } />
            <Route path="/orders/:orderType/:orderId" element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/slabs" element={
              <ProtectedRoute>
                <SlabsPage />
              </ProtectedRoute>
            } />
            <Route path="/store/manage/legacy" element={
              <ProtectedRoute>
                <StoreManagePage />
              </ProtectedRoute>
            } />
            <Route path="/store/manage" element={
              <ProtectedRoute>
                <StoreManageLayout />
              </ProtectedRoute>
            }>
              <Route index element={<StoreOverviewPage />} />
              <Route path="inventory" element={<StoreInventoryPage />} />
              <Route path="pricing" element={<StorePricingPage />} />
              <Route path="orders" element={<StoreOrdersPage />} />
              <Route path="storefront" element={<StoreStorefrontPage />} />
            </Route>
            <Route path="/store/picking/:id" element={
              <ProtectedRoute>
                <StorePickingListPage />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/tampermonkey" element={
              <ProtectedRoute>
                <TampermonkeyGuidePage />
              </ProtectedRoute>
            } />
            <Route path="/price-scans" element={
              <ProtectedRoute>
                <PriceScanStatusPage />
              </ProtectedRoute>
            } />
            <Route path="/trading" element={
              <ProtectedRoute>
                <TradingDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/trading/card/:cardId" element={
              <ProtectedRoute>
                <TradingCardPage />
              </ProtectedRoute>
            } />
            <Route path="/trading/portfolio" element={
              <ProtectedRoute>
                <TradingPortfolioPage />
              </ProtectedRoute>
            } />
            <Route path="/trading/scanner" element={
              <ProtectedRoute>
                <TradingScannerPage />
              </ProtectedRoute>
            } />
            <Route path="/trading/heatmap" element={
              <ProtectedRoute>
                <TradingHeatmapPage />
              </ProtectedRoute>
            } />
            <Route path="/trading/builder" element={
              <ProtectedRoute>
                <TradingBuilderPage />
              </ProtectedRoute>
            } />
            <Route path="/trading/sets/:setId" element={
              <ProtectedRoute>
                <TradingSetBreakdownPage />
              </ProtectedRoute>
            } />
            <Route path="/trading/cardtrader" element={
              <ProtectedRoute>
                <TradingCardTraderPage />
              </ProtectedRoute>
            } />
            <Route path="/wants" element={
              <ProtectedRoute>
                <WantsPage />
              </ProtectedRoute>
            } />
            <Route path="/wants/:id" element={
              <ProtectedRoute>
                <WantsDetailPage />
              </ProtectedRoute>
            } />
          </Route>

          {/* Admin routes - outside of main layout */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="leader-breakdowns" element={<AdminLeaderBreakdowns />} />
            <Route path="leader-breakdowns/:id/chart" element={<AdminLeaderBreakdownChart />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="blueprints" element={<AdminBlueprintBrowser />} />
            <Route path="cards" element={<AdminCardManager />} />
            <Route path="feedback" element={<AdminFeedback />} />
          </Route>
          </Routes>
        </Suspense>
      </EasterEggProvider>
    </AuthProvider>
  )
}

export default App
